'use client';

import { useState, useEffect } from 'react';
import NavBar from '../home-page/components/NavBar';
import FooterSection from '../home-page/components/FooterSection';
import { generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { aboutService } from '@/lib/services/aboutService';
import { teamService, type TeamMember } from '@/lib/services/teamService';
import Link from 'next/link';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';


export default function AboutUsPage() {
  const [loading, setLoading] = useState(true);
  const [companyStory, setCompanyStory] = useState<any>(null);
  const [mission, setMission] = useState<any>(null);
  const [vision, setVision] = useState<any>(null);
  const [values, setValues] = useState<any>(null);
  const [timeline, setTimeline] = useState<any>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      const [storyData, missionData, visionData, valuesData, timelineData, teamData] = await Promise.all([
      aboutService.getByKey('company_story'),
      aboutService.getByKey('mission'),
      aboutService.getByKey('vision'),
      aboutService.getByKey('values'),
      aboutService.getByKey('timeline'),
      teamService.getActive()]
      );

      if (storyData) setCompanyStory(storyData.content);
      if (missionData) setMission(missionData.content);
      if (visionData) setVision(visionData.content);
      if (valuesData) setValues(valuesData.content);
      if (timelineData) setTimeline(timelineData.content);
      setTeamMembers(teamData.slice(0, 3)); // Show first 3 team members
    } catch (error) {
      console.error('Failed to load about content:', error);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
  { name: 'Home', url: `${SITE_URL}/home-page` },
  { name: 'About Us', url: `${SITE_URL}/about-us` }]
  );

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'GoGedit',
    url: SITE_URL,
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1134114a2-1771779843742.png",
    description: 'Ontario\'s leading on-demand moving and delivery platform providing reliable furniture delivery, appliance moving, and marketplace pickup services',
    foundingDate: '2020',
    founders: [
    {
      '@type': 'Person',
      name: 'Alain Maisonneuve',
      jobTitle: 'Founder & CEO'
    }],

    sameAs: [
    'https://www.facebook.com/gogedit',
    'https://twitter.com/gogedit',
    'https://www.linkedin.com/company/gogedit',
    'https://www.instagram.com/gogedit'],

    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      areaServed: 'CA-ON',
      availableLanguage: ['English', 'French']
    }
  };

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About GoGedit',
    description: 'Learn about GoGedit, the on-demand mobile app revolutionizing urban logistics',
    url: `${SITE_URL}/about-us`,
    mainEntity: {
      '@type': 'Organization',
      name: 'GoGedit'
    }
  };

  const getIconSvg = (iconName: string) => {
    const icons: {[key: string]: string;} = {
      users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
      shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      lightbulb: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      eye: 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
    };
    return icons[iconName] || icons.users;
  };

  if (loading) {
    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
        
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
        
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }} />
        
        <div className="min-h-screen bg-white">
          <NavBar />
          <div className="pt-24 pb-16 flex items-center justify-center">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-body-text">Loading content...</p>
            </div>
          </div>
          <FooterSection />
        </div>
      </>);

  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      <div className="min-h-screen bg-white">
        <NavBar />
        
        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="text-center mb-16">
              <h1 className="text-5xl font-bold text-navy-dark mb-6">About GoGedit</h1>
              <p className="text-xl text-body-text max-w-3xl mx-auto">
                Redefining Urban Logistics
              </p>
            </div>

            {/* Company Story */}
            {companyStory &&
            <div className="mb-16">
                <h2 className="text-3xl font-bold text-navy-dark mb-6 text-center">{companyStory.heading}</h2>
                <p className="text-lg text-body-text max-w-4xl mx-auto text-center leading-relaxed">
                  {companyStory.content}
                </p>
              </div>
            }

            {/* Mission & Vision */}
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              {mission &&
              <div className="bg-purple-light rounded-2xl p-8">
                  <h2 className="text-3xl font-bold text-navy-dark mb-4">{mission.heading}</h2>
                  <p className="text-lg text-body-text leading-relaxed">
                    {mission.content}
                  </p>
                </div>
              }
              {vision &&
              <div className="bg-green-light rounded-2xl p-8">
                  <h2 className="text-3xl font-bold text-navy-dark mb-4">{vision.heading}</h2>
                  <p className="text-lg text-body-text leading-relaxed">
                    {vision.content}
                  </p>
                </div>
              }
            </div>

            {/* Core Values */}
            {values && values.values &&
            <div className="mb-16">
                <h2 className="text-3xl font-bold text-navy-dark mb-8 text-center">{values.heading}</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {values.values.map((value: any, index: number) =>
                <div key={index} className="text-center">
                      <div className="w-16 h-16 bg-brand-purple rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={getIconSvg(value.icon)} />
                        </svg>
                      </div>
                      <h3 className="text-xl font-bold text-navy-dark mb-2">{value.title}</h3>
                      <p className="text-body-text">{value.description}</p>
                    </div>
                )}
                </div>
              </div>
            }

            {/* Timeline */}
            {timeline && timeline.milestones &&
            <div className="mb-16">
                <h2 className="text-3xl font-bold text-navy-dark mb-8 text-center">{timeline.heading}</h2>
                <div className="max-w-4xl mx-auto">
                  <div className="space-y-8">
                    {timeline.milestones.map((milestone: any, index: number) =>
                  <div key={index} className="flex gap-6">
                        <div className="flex-shrink-0">
                          <div className="w-20 h-20 bg-brand-purple rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-lg">{milestone.year}</span>
                          </div>
                        </div>
                        <div className="flex-grow pt-2">
                          <h3 className="text-2xl font-bold text-navy-dark mb-2">{milestone.title}</h3>
                          <p className="text-lg text-body-text">{milestone.description}</p>
                        </div>
                      </div>
                  )}
                  </div>
                </div>
              </div>
            }

            {/* Team Preview */}
            {teamMembers.length > 0 &&
            <div className="bg-purple-light rounded-2xl p-8 text-center">
                <h2 className="text-3xl font-bold text-navy-dark mb-6">Meet Our Team</h2>
                <p className="text-lg text-body-text mb-8">
                  Led by experienced professionals passionate about revolutionizing urban logistics
                </p>
                <div className="grid md:grid-cols-3 gap-8 mb-8">
                  {teamMembers.map((member) =>
                <Link key={member.id} href={`/team#${member.id}`} className="bg-white rounded-xl p-6 block hover:shadow-md transition-shadow cursor-pointer">
                      {member.avatarUrl ?
                  <img
                    src={member.avatarUrl}
                    alt={member.fullName}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-4" /> :


                  <div className="w-24 h-24 bg-brand-purple rounded-full flex items-center justify-center mx-auto mb-4">
                          <span className="text-white text-2xl font-bold">
                            {member.fullName.split(' ').map((n) => n[0]).join('')}
                          </span>
                        </div>
                  }
                      <h3 className="text-xl font-bold text-navy-dark mb-1">{member.fullName}</h3>
                      <p className="text-brand-purple font-medium">{member.jobTitle}</p>
                    </Link>
                )}
                </div>
                <Link
                href="/team"
                className="inline-block bg-brand-purple text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all">
                
                  View Full Team
                </Link>
              </div>
            }
          </div>
        </div>

        <FooterSection />
      </div>
    </>);

}